import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AppState, AppUserState } from '../../shared/stores/states/app.state';
import { select, Store } from '@ngrx/store';
import { selectDraftBoardsCount } from '../../shared/stores/selectors/session.selectors';
import { loadDraftBoards } from '../../shared/stores/actions/session.actions';
import { SubscriptionsManager } from '../../shared/helpers/subscriptions-manager';
import { selectCurrentUser } from '../../shared/stores/selectors/user.selectors';
import { SidebarService } from '../../shared/services/sidebar.service';
import { ReportMenu } from 'src/app/shared/entities/report-menu.entity';
import { distinctUntilChanged, filter } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { loadedRoutes, selectSelectedRoute } from 'src/app/shared/stores/selectors/navigation.selector';
import { createUrlFriendlyName } from 'src/app/shared/utils/url-utils';
import * as NavigationActions from 'src/app/shared/stores/actions/navigation.actions';
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  currentUser!: AppUserState;
  isSlimMode = false;
  draftCounter = 0;

  isLoading = false;
  navigation: ReportMenu[] = [];
  selectedRoute!: ReportMenu;

  missingOrganization = true;
  boardsEnabled: boolean = false;
  hoveredSlimLabel: string | null = null;
  flyoutLeft = 0;
  flyoutTop = 0;

  readonly labelWhatsNewBBS = "What's New";
  private static readonly SIDEBAR_LOADING_FALLBACK_MS = 8000;

  private subsManager = new SubscriptionsManager();
  private loadingFallbackTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastPowerBiNavigationRoleDispatched: string | null = null;
  private powerBiNavigationSettled = false;

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private sidebarService: SidebarService,
    private cdr: ChangeDetectorRef,
    private actions$: Actions
  ) {
    this.subsManager.add(
      this.store.select(selectDraftBoardsCount).subscribe(count => {
        this.draftCounter = count || 0;
      })
    );

    this.subsManager.add(
      this.store.select(selectCurrentUser).subscribe(currentUser => {
        this.currentUser = currentUser;
        if (currentUser) {
          this.missingOrganization = currentUser.organization?.length === 0;
          this.boardsEnabled = !this.missingOrganization;
        } else {
          this.missingOrganization = true;
          this.boardsEnabled = false;
        }
        this.dispatchLoadNavigation();
        this.updateSidebarLoadingState();
      })
    );

    this.subsManager.add(
      this.store.pipe(select(loadedRoutes), distinctUntilChanged()).subscribe((routes) => {
        this.navigation = routes;
        if (routes.length) {
          this.syncSelectedRouteFromUrl();
        }
        this.updateSidebarLoadingState();
      }),
    );

    this.subsManager.add(
      this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd)
      ).subscribe(() => {
        this.dispatchLoadNavigation();
        this.syncSelectedRouteFromUrl();
        this.updateSidebarLoadingState();
      })
    );
    this.subsManager.add(
      this.store.pipe(
        select(selectSelectedRoute),
        distinctUntilChanged()).subscribe((route) => {
          if (route) {
            this.selectedRoute = route;
          }
        }));

    this.subsManager.add(
      this.actions$.pipe(
        ofType(
          NavigationActions.loadNavigationSuccess,
          NavigationActions.loadNavigationFailure
        ),
      ).subscribe(() => {
        this.powerBiNavigationSettled = true;
        this.updateSidebarLoadingState();
      })
    );
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.sidebarService.checkScreenSize(event.target.innerWidth);
  }

  ngOnInit(): void {
    this.subsManager.add(
      this.sidebarService.isSlimMode$.subscribe(isSlim => {
        setTimeout(() => {
          this.isSlimMode = isSlim;
          this.cdr.markForCheck();
        }, 0);
      })
    );
  }
  ngOnDestroy(): void {
    if (this.loadingFallbackTimeoutId != null) {
      clearTimeout(this.loadingFallbackTimeoutId);
      this.loadingFallbackTimeoutId = null;
    }
    this.subsManager.unsubscribeAll();
  }
  ngAfterViewInit(): void {
    this.dispatchLoadNavigation();
    this.loadDraftBoards();

    this.loadingFallbackTimeoutId = setTimeout(() => {
      if (
        this.getCurrentPath() === '/pb' &&
        !this.missingOrganization &&
        this.isLoading
      ) {
        this.powerBiNavigationSettled = true;
        this.updateSidebarLoadingState();
      }
      this.loadingFallbackTimeoutId = null;
    }, SidebarComponent.SIDEBAR_LOADING_FALLBACK_MS);
  }


  loadDraftBoards() {
    this.store.dispatch(loadDraftBoards());
  }

  toggleSlimMode() {
    this.sidebarService.toggleSlimMode();
  }


  isActive(routePath: string): boolean {
    const currentPath = `/${this.router.url.split('?')[0].split('/')[1]}`;
    return currentPath === routePath;
  }
  isActiveReport(uuid: string | null): boolean {
    if (!uuid) {
      return false;
    }
    const currentPath = this.getCurrentPath();
    if (currentPath !== '/pb') {
      return false;
    }
    if (uuid === this.selectedRoute?.uuid) {
      return true;
    }

    const reportParam = this.getReportParamFromUrl();
    if (reportParam) {
      const nav = this.navigation.find((r) => r.uuid === uuid);
      return nav ? createUrlFriendlyName(nav.menuName) === reportParam : false;
    }
    return false;
  }
  private loadPowerBiNavigation(): boolean {
    const hasOrg = (this.currentUser?.organization?.length ?? 0) > 0;
    const onPb = this.getCurrentPath() === '/pb';
    return hasOrg || onPb;
  }

  private updateSidebarLoadingState(): void {
    const onPb = this.getCurrentPath() === '/pb';
    const showSkeleton =
      onPb &&
      !this.missingOrganization &&
      !!this.currentUser?.role &&
      this.navigation.length === 0 &&
      !this.powerBiNavigationSettled;
    this.isLoading = showSkeleton;
  }

  private dispatchLoadNavigation(): void {
    if (!this.currentUser?.role) {
      this.lastPowerBiNavigationRoleDispatched = null;
      return;
    }
    if (!this.loadPowerBiNavigation()) {
      this.lastPowerBiNavigationRoleDispatched = null;
      return;
    }
    if (this.lastPowerBiNavigationRoleDispatched === this.currentUser.role) {
      return;
    }
    this.lastPowerBiNavigationRoleDispatched = this.currentUser.role;
    this.powerBiNavigationSettled = false;
    this.store.dispatch(
      NavigationActions.loadNavigation({ role: this.currentUser.role }),
    );
    this.updateSidebarLoadingState();
  }
  onReportClick(route: ReportMenu) {
    this.store.dispatch(NavigationActions.selectRoute({ route }));
    this.selectedRoute = route;

    const urlFriendlyName = createUrlFriendlyName(route.menuName);

    if (!this.isActive('')) {

      this.router.navigate(['/pb'], {
        queryParams: { report: urlFriendlyName },
        replaceUrl: true
      });
    } else {

      this.router.navigate([], {
        relativeTo: null,
        queryParams: { report: urlFriendlyName },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
  }
  hasRole(role: string): boolean {
    return this.currentUser?.role?.toLowerCase() === role.toLowerCase();
  }

  private syncSelectedRouteFromUrl(): void {
    const currentPath = this.getCurrentPath();
    if (currentPath !== '/pb') return;
    const reportParam = this.getReportParamFromUrl();
    if (!reportParam || !this.navigation.length) return;
    const match = this.navigation.find(
      (r) => createUrlFriendlyName(r.menuName) === reportParam
    );
    if (match && this.selectedRoute?.uuid !== match.uuid) {
      this.store.dispatch(NavigationActions.selectRoute({ route: match }));
    }
  }

  private getCurrentPath(): string {
    const segment = this.router.url.split('?')[0].split('/')[1] ?? '';
    return segment ? `/${segment}` : '/';
  }

  private getReportParamFromUrl(): string | null {
    const report = this.router.parseUrl(this.router.url).queryParams['report'];
    return report ?? null;
  }

  showSlimFlyout(event: MouseEvent, label: string): void {
    if (!this.isSlimMode) return;
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    this.flyoutLeft = rect.right + 8;
    this.flyoutTop = rect.top + rect.height / 2;
    this.hoveredSlimLabel = label;
  }

  hideSlimFlyout(): void {
    this.hoveredSlimLabel = null;
  }
}
